/* eslint-disable max-lines */
import type Konva from "konva";
import { useEffect, useMemo, useRef, useState } from "react";
import { Group, Rect, Image as RImage, Transformer } from "react-konva";

import { trackCompositeRenderFailed } from "@design-lab/shared/lib/analytics/composite-render";
import { getDrawSourceDimensions, safeDrawImage } from "@design-lab/shared/lib/safeDrawImage";

type RectBox = { x: number; y: number; width: number; height: number };

export type CropTransformerProps = {
  node: Konva.Image | null | undefined;
  stroke?: string;
  strokeWidth?: number;
  printableArea: { left: number; top: number; width: number; height: number };
  onCropCommit: (croppedImageData: {
    imageData: HTMLCanvasElement;
    newDimensions: { width: number; height: number; x: number; y: number };
    cropInfo: {
      cropX: number;
      cropY: number;
      cropWidth: number;
      cropHeight: number;
      originalWidth: number;
      originalHeight: number;
    };
  }) => void;
  onTransformingChange?: (transforming: boolean) => void;
};

// eslint-disable-next-line max-lines-per-function
export function CropTransformer({
  node,
  stroke = "#4864F9",
  strokeWidth = 0.95,
  printableArea,
  onCropCommit,
  onTransformingChange
}: CropTransformerProps) {
  const rectRef = useRef<Konva.Rect>(null);
  const trRef = useRef<Konva.Transformer>(null);
  const [rect, setRect] = useState<RectBox | null>(null);
  const [originSize, setOriginSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0
  });
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [nodePos, setNodePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const ratioRef = useRef<number>(1);
  const startRectRef = useRef<RectBox | null>(null);

  const domImg = node?.image() as HTMLImageElement | undefined;

  // ratio in parent-local coordinates: canvas units in this group per image pixel
  const ratio = useMemo(() => {
    if (!node || !domImg) return 1;
    const cropW = node.cropWidth() || domImg.width || 1;
    const viewWLocal = node.width() || 1;
    return viewWLocal / cropW;
  }, [node, domImg]);

  // Always use current DOM image dimensions (accounts for previous crops)
  const maxW = useMemo(() => {
    if (!domImg || !node) return 0;
    // Use current image width scaled to node dimensions
    const currentCropWidth = node.cropWidth() || domImg.width;
    const nodeWidth = node.width() || 1;
    const currentRatio = nodeWidth / currentCropWidth;
    return domImg.width * currentRatio;
  }, [domImg, node]);

  const maxH = useMemo(() => {
    if (!domImg || !node) return 0;
    // Use current image height scaled to node dimensions
    const currentCropWidth = node.cropWidth() || domImg.width;
    const nodeWidth = node.width() || 1;
    const currentRatio = nodeWidth / currentCropWidth;
    return domImg.height * currentRatio;
  }, [domImg, node]);

  useEffect(() => {
    if (!node || !domImg) return;

    // Store ratio for consistent calculations
    ratioRef.current = ratio;

    const cropX = (node.cropX() || 0) * ratio;
    const cropY = (node.cropY() || 0) * ratio;
    const cropW = (node.cropWidth() || domImg.width) * ratio;
    const cropH = (node.cropHeight() || domImg.height) * ratio;
    const pos = node.position();
    setNodePos({ x: pos.x, y: pos.y });
    setOriginSize({ width: domImg.width * ratio, height: domImg.height * ratio });

    setRect({ x: cropX, y: cropY, width: cropW, height: cropH });

    requestAnimationFrame(() => {
      if (rectRef.current) trRef.current?.nodes([rectRef.current]);
    });
  }, [node, domImg, ratio]);

  const applyToImage = (next: RectBox) => {
    if (!node) return;
    const cropX = next.x / ratio;
    const cropY = next.y / ratio;
    const cropWidth = next.width / ratio;
    const cropHeight = next.height / ratio;

    node.setAttrs({ cropX, cropY, cropWidth, cropHeight });
    node.getLayer()?.batchDraw();
  };

  const handleTransform = () => {
    if (!rectRef.current) return;
    const raw = {
      x: rectRef.current.x(),
      y: rectRef.current.y(),
      width: rectRef.current.width() * rectRef.current.scaleX(),
      height: rectRef.current.height() * rectRef.current.scaleY()
    };

    // Apply constraints here
    const MIN = 20;

    // Determine which edges moved by comparing with current rect
    const currentRect = rect || { x: 0, y: 0, width: MIN, height: MIN };
    const leftMoved =
      Math.abs(raw.x - currentRect.x) >
      Math.abs(raw.x + raw.width - (currentRect.x + currentRect.width));
    const topMoved =
      Math.abs(raw.y - currentRect.y) >
      Math.abs(raw.y + raw.height - (currentRect.y + currentRect.height));

    let clampedX = raw.x;
    let clampedY = raw.y;
    let clampedWidth = raw.width;
    let clampedHeight = raw.height;

    // Handle horizontal constraints
    if (leftMoved) {
      // Left edge moving: keep right edge fixed
      const rightEdge = currentRect.x + currentRect.width;
      clampedX = Math.max(0, Math.min(raw.x, rightEdge - MIN));
      clampedWidth = rightEdge - clampedX;
    } else {
      // Right edge moving: keep left edge fixed, clamp right edge
      clampedX = Math.max(0, Math.min(currentRect.x, maxW - MIN));
      const maxRight = Math.min(raw.x + raw.width, maxW);
      clampedWidth = Math.max(MIN, maxRight - clampedX);
    }

    // Handle vertical constraints
    if (topMoved) {
      // Top edge moving: keep bottom edge fixed
      const bottomEdge = currentRect.y + currentRect.height;
      clampedY = Math.max(0, Math.min(raw.y, bottomEdge - MIN));
      clampedHeight = bottomEdge - clampedY;
    } else {
      // Bottom edge moving: keep top edge fixed, clamp bottom edge
      clampedY = Math.max(0, Math.min(currentRect.y, maxH - MIN));
      const maxBottom = Math.min(raw.y + raw.height, maxH);
      clampedHeight = Math.max(MIN, maxBottom - clampedY);
    }

    const clamped = {
      x: clampedX,
      y: clampedY,
      width: clampedWidth,
      height: clampedHeight
    } as RectBox;

    rectRef.current.setAttrs({ ...clamped, scaleX: 1, scaleY: 1 });
    setRect(clamped);
    applyToImage(clamped);
  };

  const handleEnd = async () => {
    if (!rectRef.current || !node || !domImg) return;

    const cropRect = {
      x: rectRef.current.x(),
      y: rectRef.current.y(),
      width: rectRef.current.width(),
      height: rectRef.current.height()
    } as RectBox;

    // Only commit if there is a meaningful change from the transform start
    const EPS = 0.5; // pixels tolerance in local coordinates
    const start = startRectRef.current;
    if (
      start &&
      Math.abs(cropRect.x - start.x) < EPS &&
      Math.abs(cropRect.y - start.y) < EPS &&
      Math.abs(cropRect.width - start.width) < EPS &&
      Math.abs(cropRect.height - start.height) < EPS
    ) {
      setShowPreview(false);
      // Ensure base image is visible again
      node.show();
      return;
    }

    // Convert crop rect to original image coordinates
    const currentRatio = ratioRef.current;
    const finalCropX = cropRect.x / currentRatio;
    const finalCropY = cropRect.y / currentRatio;
    const finalCropWidth = cropRect.width / currentRatio;
    const finalCropHeight = cropRect.height / currentRatio;

    // Bail out (and re-show base image) if the source has zero natural dimensions —
    // drawing from a 0-dim image would throw IndexSizeError in some Chromium variants.
    const { width: srcWidth, height: srcHeight } = getDrawSourceDimensions(domImg);
    if (srcWidth <= 0 || srcHeight <= 0) {
      trackCompositeRenderFailed({
        reason: "zero_dimensions",
        site: "CropTransformer.handleEnd",
        sourceWidth: srcWidth,
        sourceHeight: srcHeight
      });
      setShowPreview(false);
      node.show();
      if (onTransformingChange) onTransformingChange(false);
      return;
    }

    // Create cropped image data
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = finalCropWidth;
    canvas.height = finalCropHeight;

    // Draw cropped portion of original image
    const drawn = safeDrawImage({
      ctx,
      source: domImg,
      site: "CropTransformer.handleEnd",
      args: [
        finalCropX,
        finalCropY,
        finalCropWidth,
        finalCropHeight,
        0,
        0,
        finalCropWidth,
        finalCropHeight
      ]
    });
    if (!drawn) {
      setShowPreview(false);
      node.show();
      if (onTransformingChange) onTransformingChange(false);
      return;
    }

    // Calculate dimensions to fit printable area
    const aspectRatio = finalCropHeight / finalCropWidth;
    const maxWidth = printableArea.width;
    const maxHeight = printableArea.height;
    const maxAllowedWidth = Math.min(maxWidth, maxHeight / aspectRatio);

    const newWidth = Math.min(maxAllowedWidth, finalCropWidth);
    const newHeight = newWidth * aspectRatio;

    // Center in printable area
    const newX = printableArea.left + (printableArea.width - newWidth) / 2;
    const newY = printableArea.top;

    // Commit the cropped image data
    onCropCommit({
      imageData: canvas,
      newDimensions: { width: newWidth, height: newHeight, x: newX, y: newY },
      cropInfo: {
        cropX: finalCropX,
        cropY: finalCropY,
        cropWidth: finalCropWidth,
        cropHeight: finalCropHeight,
        originalWidth: domImg.width,
        originalHeight: domImg.height
      }
    });

    setShowPreview(false);
    // Reveal base image after committing crop
    // Optimistically set the base node to the final display dimensions immediately
    node.setAttrs({ x: newX, y: newY, width: newWidth, height: newHeight });
    node.getLayer()?.batchDraw();
    node.show();
    if (onTransformingChange) onTransformingChange(false);
  };

  if (!rect) return null;

  return (
    <Group x={nodePos.x} y={nodePos.y}>
      {/* Preview image clipped to crop rect - only during active transform */}
      {showPreview && (
        <Group clip={{ x: rect.x, y: rect.y, width: rect.width, height: rect.height }}>
          <RImage image={domImg} width={originSize.width} height={originSize.height} />
        </Group>
      )}

      <Group>
        <Rect
          ref={rectRef}
          x={rect.x}
          y={rect.y}
          width={rect.width}
          height={rect.height}
          draggable={false}
          stroke={stroke}
          strokeWidth={strokeWidth}
          onTransform={handleTransform}
          onTransformStart={() => {
            if (!rectRef.current) return;
            startRectRef.current = {
              x: rectRef.current.x(),
              y: rectRef.current.y(),
              width: rectRef.current.width(),
              height: rectRef.current.height()
            };
            setShowPreview(true);
            // Hide the base image only during active crop transform to avoid double-visuals
            if (node) node.hide();
            if (onTransformingChange) onTransformingChange(true);
          }}
          onTransformEnd={handleEnd}
        />
      </Group>

      <Transformer
        ref={trRef}
        rotateEnabled={false}
        flipEnabled={false}
        keepRatio={false}
        enabledAnchors={[
          "top-left",
          "top-right",
          "bottom-left",
          "bottom-right",
          "middle-left",
          "middle-right",
          "top-center",
          "bottom-center"
        ]}
        anchorFill="white"
        anchorStroke={stroke}
        anchorSize={12}
        anchorCornerRadius={0}
        anchorStrokeWidth={strokeWidth}
      />
    </Group>
  );
}
